import { useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "sonner";
import Header from "./components/Header";
import Footer from "./components/Footer";
import AnnouncementBanner from "./components/AnnouncementBanner";
import PromoAlertBar from "./components/PromoAlertBar";
import GoogleSiteVerification from "./components/GoogleSiteVerification";
import HomePage from "./pages/HomePage";
import PlayBasedSupportPage from "./pages/PlayBasedSupportPage";
import AboutPage from "./pages/AboutPage";
import ContactPage from "./pages/ContactPage";
import ProgramsHubPage from "./pages/ProgramsHubPage";
import ProgramLandingPage from "./pages/ProgramLandingPage";
import TrialClassFunnelPage from "./pages/TrialClassFunnelPage";
import CampsListPage from "./pages/CampsListPage";
import CampDetailPage from "./pages/CampDetailPage";
import ClassesPage from "./pages/ClassesPage";
import BookingSuccessPage from "./pages/BookingSuccessPage";
import BookingCancelledPage from "./pages/BookingCancelledPage";
import PractitionerPage from "./pages/PractitionerPage";
import AdminTherapyPage from "./admin/AdminTherapyPage";

import { AdminAuthProvider } from "./admin/AdminAuthContext";
import ProtectedRoute from "./admin/ProtectedRoute";
import AdminLayout from "./admin/AdminLayout";
import AdminLoginPage from "./admin/AdminLoginPage";
import AdminDashboard from "./admin/AdminDashboard";
import AdminHomepagePage from "./admin/AdminHomepagePage";
import AdminProgramsPage from "./admin/AdminProgramsPage";
import AdminCampsPage from "./admin/AdminCampsPage";
import AdminFaqsPage from "./admin/AdminFaqsPage";
import AdminTestimonialsPage from "./admin/AdminTestimonialsPage";
import AdminContactInfoPage from "./admin/AdminContactInfoPage";
import AdminAnnouncementsPage from "./admin/AdminAnnouncementsPage";
import AdminInquiriesPage from "./admin/AdminInquiriesPage";
import AdminAnalyticsPage from "./admin/AdminAnalyticsPage";
import AdminAccountPage from "./admin/AdminAccountPage";
import { track } from "./lib/analytics";

function TrackPageViews() {
  const { pathname } = useLocation();
  useEffect(() => {
    if (pathname.startsWith("/admin")) return;
    const t = setTimeout(() => {
      track("page_view");
      if (pathname.startsWith("/programs/")) {
        track("program_view", { program_slug: pathname.split("/")[2] });
      } else if (pathname.startsWith("/camps/")) {
        track("camp_view", { camp_slug: pathname.split("/")[2] });
      } else if (pathname === "/play-based-support") {
        track("play_based_support_view");
      } else if (pathname === "/trial-class") {
        track("trial_page_view");
      }
    }, 400);
    return () => clearTimeout(t);
  }, [pathname]);
  return null;
}

function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) return; // let anchor links scroll naturally
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [pathname, hash]);
  return null;
}

function PublicShell({ children }) {
  return (
    <>
      <GoogleSiteVerification />
      <PromoAlertBar />
      <AnnouncementBanner />
      <Header />
      <main>{children}</main>
      <Footer />
    </>
  );
}

function Admin({ children }) {
  return (
    <ProtectedRoute>
      <AdminLayout>{children}</AdminLayout>
    </ProtectedRoute>
  );
}

function App() {
  return (
    <HelmetProvider>
      <AdminAuthProvider>
        <div className="App font-body text-slate-900">
          <BrowserRouter>
            <ScrollToTop />
            <TrackPageViews />
            <Routes>
              {/* Public site (Nexora) */}
              <Route path="/" element={<PublicShell><HomePage /></PublicShell>} />
              <Route path="/about" element={<PublicShell><AboutPage /></PublicShell>} />
              <Route path="/play-based-support" element={<PublicShell><PlayBasedSupportPage /></PublicShell>} />
              <Route path="/programs" element={<PublicShell><ProgramsHubPage /></PublicShell>} />
              <Route path="/programs/:slug" element={<PublicShell><ProgramLandingPage /></PublicShell>} />
              <Route path="/camps" element={<PublicShell><CampsListPage /></PublicShell>} />
              <Route path="/camps/:slug" element={<PublicShell><CampDetailPage /></PublicShell>} />
              <Route path="/classes" element={<PublicShell><ClassesPage /></PublicShell>} />
              <Route path="/booking/success" element={<PublicShell><BookingSuccessPage /></PublicShell>} />
              <Route path="/booking/cancelled" element={<PublicShell><BookingCancelledPage /></PublicShell>} />
              <Route path="/play-based-support/practitioners/:id" element={<PublicShell><PractitionerPage /></PublicShell>} />
              <Route path="/trial-class" element={<PublicShell><TrialClassFunnelPage /></PublicShell>} />
              <Route path="/contact" element={<PublicShell><ContactPage /></PublicShell>} />

              {/* Legacy public routes — quietly redirect to home so old bookmarks don't 404 */}
              <Route path="/faq" element={<Navigate to="/" replace />} />

              {/* Admin */}
              <Route path="/admin/login" element={<AdminLoginPage />} />
              <Route path="/admin" element={<Admin><AdminDashboard /></Admin>} />
              <Route path="/admin/homepage" element={<Admin><AdminHomepagePage /></Admin>} />
              <Route path="/admin/programs" element={<Admin><AdminProgramsPage /></Admin>} />
              <Route path="/admin/camps" element={<Admin><AdminCampsPage /></Admin>} />
              <Route path="/admin/faqs" element={<Admin><AdminFaqsPage /></Admin>} />
              <Route path="/admin/testimonials" element={<Admin><AdminTestimonialsPage /></Admin>} />
              <Route path="/admin/contact-info" element={<Admin><AdminContactInfoPage /></Admin>} />
              <Route path="/admin/therapy" element={<Admin><AdminTherapyPage /></Admin>} />
              <Route path="/admin/announcements" element={<Admin><AdminAnnouncementsPage /></Admin>} />
              <Route path="/admin/inquiries" element={<Admin><AdminInquiriesPage /></Admin>} />
              <Route path="/admin/analytics" element={<Admin><AdminAnalyticsPage /></Admin>} />
              <Route path="/admin/account" element={<Admin><AdminAccountPage /></Admin>} />

              {/* Fallback */}
              <Route path="*" element={<PublicShell><HomePage /></PublicShell>} />
            </Routes>
          </BrowserRouter>
          <Toaster position="top-right" richColors closeButton toastOptions={{ className: "font-body" }} />
        </div>
      </AdminAuthProvider>
    </HelmetProvider>
  );
}

export default App;
